import { useFeatureGate } from '@/hooks/useFeatureGate'
import { AlertCircle, BookOpen, ChevronLeft, ChevronRight, RotateCw, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useState } from 'react'

/**
 * Demo lifecycle banner. Renders nothing for paying orgs.
 *
 * - demo_active: thin top strip with days-left countdown. Amber under 7d.
 * - demo_expired: full-width banner explaining agents are paused, with
 *   a CTA to upgrade. Dashboard reads stay accessible; writes that hit
 *   requireActiveSubscription on the backend will get 403'd, which is
 *   the correct outcome.
 * - archived: nothing — the org has been shelved, the user shouldn't
 *   normally reach this in the dashboard at all (lifecycle worker
 *   archives status='archived').
 */
// localStorage key for the affiliate-banner collapsed state. Single global
// key — affiliates almost always have one sandbox. If we ever support
// multiple sandboxes per affiliate, switch this to be keyed by the active
// org id (would need plumbing from useAuth into useFeatureGate first).
const BANNER_COLLAPSE_KEY = 'll.affiliate-banner-collapsed'

export function DemoBanner() {
  const { demo, affiliateSandbox } = useFeatureGate()
  // Collapsed state. Initialized lazily from localStorage so first paint
  // uses the user's previous choice; default = expanded.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(BANNER_COLLAPSE_KEY) === '1'
    } catch {
      return false
    }
  })

  function setCollapsedPersisted(next: boolean) {
    setCollapsed(next)
    try {
      window.localStorage.setItem(BANNER_COLLAPSE_KEY, next ? '1' : '0')
    } catch { /* ignore quota errors */ }
  }

  if (affiliateSandbox?.enabled) {
    // Collapsed pill — small, hugs the right edge, click to re-expand.
    // Sticks at the very top so the affiliate can find it later. No layout
    // jump because the full banner was also a thin top strip.
    if (collapsed) {
      return (
        <>
          <div className="flex justify-end px-2 py-1 border-b bg-sky-50/60 border-sky-200/60">
            <button
              onClick={() => setCollapsedPersisted(false)}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-900 text-[11px] font-medium transition-colors"
              title="Expand affiliate demo bar"
              aria-label="Expand affiliate demo bar"
            >
              <Sparkles className="h-3 w-3" />
              <span>Demo</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          {/* Reset dialog kept available even when collapsed, but the trigger
              is hidden behind expanding first. */}
        </>
      )
    }

    // 2026-05-05: removed the banner's own reset dialog. It used to mirror
    // the affiliate dashboard's reset, but in production the in-banner
    // version stopped firing the API call (Network panel showed zero
    // requests on click). Rather than chase a duplicate-handler ghost, the
    // banner now just deep-links to /affiliate where the working reset
    // dialog lives. One reset surface, no drift.
    return (
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 text-xs border-b bg-sky-50 border-sky-200 text-sky-950">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium shrink-0">Affiliate demo workspace</span>
          <span className="text-sky-900/70 hidden md:inline truncate">
            {affiliateSandbox.notice || 'Sample data only. Real referred clients are tracked in Affiliate Dashboard.'}
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link
            to="/affiliate/walkthrough"
            className="inline-flex items-center gap-1 underline hover:no-underline"
            title="How-to guide and FAQ for running affiliate demos"
          >
            <BookOpen className="h-3 w-3" />
            <span className="hidden sm:inline">Walkthrough</span>
          </Link>
          <Link
            to="/affiliate?reset=1"
            className="inline-flex items-center gap-1 underline hover:no-underline"
            title="Reset wipes wizard data and lets you start fresh for the next prospect"
          >
            <RotateCw className="h-3 w-3" />
            <span className="hidden sm:inline">Reset demo</span>
          </Link>
          <Link to="/affiliate" className="underline hover:no-underline hidden sm:inline">
            Affiliate Dashboard
          </Link>
          <button
            onClick={() => setCollapsedPersisted(true)}
            className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-sky-100 transition-colors"
            title="Hide this bar"
            aria-label="Hide affiliate demo bar"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    )
  }
  if (!demo) return null

  if (demo.status === 'active') {
    const urgent = demo.daysLeft <= 7
    return (
      <div
        className={`flex items-center justify-between gap-3 px-4 py-2 text-xs border-b ${
          urgent
            ? 'bg-amber-50 border-amber-200 text-amber-900'
            : 'bg-brand-royal/5 border-brand-royal/15 text-brand-royal'
        }`}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium">
            Demo · {demo.daysLeft} {demo.daysLeft === 1 ? 'day' : 'days'} left
          </span>
          <span className="text-muted-foreground hidden sm:inline">
            {urgent
              ? 'Convert to Associate to keep your agents running past expiry.'
              : 'Full Partner-tier access while your demo is active.'}
          </span>
        </div>
        <Link
          to="/settings/billing"
          className={`underline hover:no-underline ${urgent ? 'font-semibold' : ''}`}
        >
          Convert
        </Link>
      </div>
    )
  }

  if (demo.status === 'expired') {
    return (
      <div className="flex items-start gap-3 px-4 py-3 bg-rose-50 border-b border-rose-200 text-sm text-rose-900">
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold">Demo expired — your agents are paused.</p>
          <p className="text-xs mt-0.5 text-rose-800/80">
            Calls and chats won't be answered until you convert. Your data is preserved for 30 days, then automatically archived.
          </p>
        </div>
        <Link
          to="/settings/billing"
          className="shrink-0 inline-flex items-center px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
        >
          Convert to Associate
        </Link>
      </div>
    )
  }

  return null
}
